import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { Message, Session, UnreadConversation } from 'talkjs/all';
import {
  summaryFromStoredSummary,
  summaryFromTalkJsConversationSnapshot,
  summaryFromTalkJsMessage,
} from './summaries';
import type { ConversationsContextValue, StoredSummariesMap } from './types';

const PreinitializedImplementation = {
  getConversationSummary: () => {
    throw new Error(
      "getConversationSummary called outside of ConversationsProvider's context",
    );
  },
  ensureConversationSummary: () => {
    throw new Error(
      "ensureConversationSummary called outside of ConversationsProvider's context",
    );
  },
  getUnreadConversationCount: () => {
    throw new Error(
      "getUnreadConversationCount called outside of ConversationsProvider's context",
    );
  },
} as const;

const ConversationsContext = createContext<ConversationsContextValue>(
  PreinitializedImplementation,
);

/**
 * Thin wrapper around `session.onMessage`. Manages subscription lifetime;
 * the caller decides what the event means.
 */
function useTalkJsMessageSubscription(
  session: Session | null,
  onMessage: (message: Message) => void,
) {
  useEffect(() => {
    if (!session) return;
    const subscription = session.onMessage(onMessage);
    return () => subscription.unsubscribe();
  }, [session, onMessage]);
}

/**
 * Thin wrapper around `session.unreads.onChange`. Manages subscription
 * lifetime; the caller decides what the event means.
 *
 * TODO: Migrate `session.unreads` to the TalkJS JavaScript Data API's
 *   `Session.subscribeConversations` once we upgrade our self-hosted TalkJS
 *   SDK (see `utils/devbox3/roles/common/vars/main.yaml` →
 *   `talkjs_image_version`). Today we use the Data API's
 *   `ConversationRef.get()` but not `Session.subscribeConversations()`.
 *   Consumers see only `ConversationSummary` + `useConversationSummary` —
 *   no TalkJS types leak across this boundary, so the eventual Data API
 *   upgrade will be contained to this file.
 */
function useTalkJsUnreadsSubscription(
  session: Session | null,
  onChange: (unreads: UnreadConversation[]) => void,
) {
  useEffect(() => {
    if (!session) return;
    // unreads.onChange: https://talkjs.com/docs/UI_Components/JavaScript/Classic/Session/#Unreads__onChange
    const subscription = session.unreads.onChange(onChange);
    return () => subscription.unsubscribe();
  }, [session, onChange]);
}

/**
 * Reducer: applies an inbound TalkJS message to the summaries cache.
 *
 * TalkJS `unreads.onChange` won't fire for a message that arrives while the
 * user already has the conversation open (TalkJS auto-marks it read), so we
 * refresh `lastMessage` here independently on every inbound message.
 */
function summariesAfterMessage(
  prev: StoredSummariesMap,
  message: Message,
): StoredSummariesMap {
  const next = new Map(prev);
  next.set(message.conversation.id, {
    isLoading: false,
    summary: summaryFromTalkJsMessage(message),
  });
  return next;
}

/**
 * Provides information about the current state of conversations. The current
 * backend is using TalkJS SDK. However, the exposed types of this provider and
 * the related hooks such as @see useConversationSummary should not expose any
 * TalkJS-specific type.
 *
 * NOTE: ConversationsProvider/useConversationSummary should work for
 *   TalkJS-based conversation. It is not coupled to interagency chats, albeit
 *   that is the only place it is currently used.
 */
export function ConversationsProvider({
  session,
  children,
}: {
  session: Session | null;
  children: ReactNode;
}) {
  // One new Map per mutation triggers every useContext consumer to re-render.
  // So we must avoid unnecessary mutations by reusing the same Map instance
  // when possible. This is especially important for the "loading" state, which
  // is the default for every conversation until its summary is fetched — if we
  // created a new Map on every render, every conversation would be "new" to
  // React on every render and every consumer would re-render on every state
  // update, even if their relevant conversations hadn't changed. List sizes are
  // small (tens of conversations per user), so per-row memoization isn't worth
  // the complexity.
  const [summaries, setSummaries] = useState<StoredSummariesMap>(
    () => new Map(),
  );
  // Lets `ensureConversationSummary` read the latest cache without depending on
  // `summaries`. Depending on it would give that callback a new identity on
  // every mutation, re-firing every consumer's mount effect on each emission.
  // The ref keeps the callback stable while still letting it skip a redundant
  // `.get()` for an already-cached conversation.
  const summariesRef = useRef(summaries);
  summariesRef.current = summaries;

  type ConversationID = string;
  const [unreads, setUnreads] = useState(new Set<ConversationID>());

  const ensureConversationSummary = useCallback(
    (conversationId: string): void => {
      // If we already have a cache entry, nothing to do:
      //   1. It's loaded (isLoading: false) and onMessage will keep it fresh, or
      //   2. Its `.get()` is still in flight (isLoading: true).
      // OR we were called twice in one react tick and summariesRef.current is
      // stale, but that's harmless: worst case we fire a second
      // `session.conversation(conversationId).get()` (which TalkJS should dedupe).
      if (summariesRef.current.has(conversationId)) {
        return;
      }

      if (!session) {
        // TODO: This is informative for SOUP based on some troubleshooting
        //   we're doing and should be low volume while Interagency Chat is
        //   still being adopted. Can be removed after we resolve
        //   https://rapidsos.atlassian.net/browse/SOUP-1343
        // eslint-disable-next-line no-console -- console.error logs to sentry
        console.error(
          `Cannot ensure conversation summary for conversation ID ${conversationId} because TalkJS session is not initialized`,
        );
        return;
      }

      setSummaries(prevSummaries => {
        // Write the loading state to the cache before firing the `.set()`/`.get()`
        // request so that any re-render between now and the fetch resolving sees
        // `{ isLoading: true }`.
        const nextSummaries = new Map(prevSummaries);
        nextSummaries.set(conversationId, {
          isLoading: true,
          summary: null,
        });
        return nextSummaries;
      });

      const conversationRef = session.conversation(conversationId);

      // `.set({})` first to ensure the current user is a participant. A
      // ConversationRef is the user's *own view* of the conversation; if they
      // aren't a participant that view doesn't exist and `.get()` returns null.
      // `.set()` adds them (a no-op if they already are) so the snapshot below
      // carries the last message and sender data.
      // https://talkjs.com/docs/Data_APIs/JavaScript/Conversations/#ConversationRef__set
      conversationRef
        .set({})
        .then(() => {
          conversationRef
            .get()
            .then(snapshot => {
              if (!snapshot) {
                // TODO: This is informative for SOUP based on some troubleshooting
                //   we're doing and should be low volume while Interagency Chat is
                //   still being adopted. Can be removed after we resolve
                //   https://rapidsos.atlassian.net/browse/SOUP-1343
                // eslint-disable-next-line no-console -- console.error logs to sentry
                console.error(
                  `TalkJS Conversation snapshot request returned null for conversation ID ${conversationId}`,
                );

                // remove it and let TalkJS fetch it again later:
                setSummaries(prevSummaries => {
                  const nextSummaries = new Map(prevSummaries);
                  nextSummaries.delete(conversationId);
                  return nextSummaries;
                });
                return;
              }
              setSummaries(prevSummaries => {
                const nextSummaries = new Map(prevSummaries);
                nextSummaries.set(conversationId, {
                  isLoading: false,
                  summary: summaryFromTalkJsConversationSnapshot(snapshot),
                });
                return nextSummaries;
              });
            })
            .catch(error => {
              // eslint-disable-next-line no-console -- console.error logs to sentry
              console.error(`Failed to fetch conversation snapshot`, {
                error,
                conversationId,
              });
              // to prevent us continually trying to fetch a conversation that's
              // errored, we cache the "not found" state. If a message is received
              // or it becomes unread, we'll get a notification from TalkJS and
              // refresh the cache.
              setSummaries(prevSummaries => {
                const nextSummaries = new Map(prevSummaries);
                nextSummaries.set(conversationId, {
                  isLoading: false,
                  summary: null,
                });
                return nextSummaries;
              });
            });
        })
        .catch(error => {
          // eslint-disable-next-line no-console -- console.error logs to sentry
          console.error(
            `Failed to set conversation for conversation ID ${conversationId}`,
            { error },
          );
          // See above catch block for rationale on caching the "not found" state.
          setSummaries(prevSummaries => {
            const nextSummaries = new Map(prevSummaries);
            nextSummaries.set(conversationId, {
              isLoading: false,
              summary: null,
            });
            return nextSummaries;
          });
        });
    },
    [session],
  );

  const handleTalkJsNewMessage = useCallback((message: Message) => {
    setSummaries(prev => summariesAfterMessage(prev, message));
  }, []);

  const handleTalkJsUnreadsChanged = useCallback(
    (unreads: UnreadConversation[]) => {
      setUnreads(new Set(unreads.map(uc => uc.conversation.id)));
    },
    [],
  );

  useTalkJsMessageSubscription(session, handleTalkJsNewMessage);
  useTalkJsUnreadsSubscription(session, handleTalkJsUnreadsChanged);

  const getUnreadConversationCount = useCallback(
    (knownConversationIds: Iterable<string>): number => {
      let count = 0;
      // `Array.from` because the project's tsconfig target (es5) doesn't allow
      // `for...of` over a bare iterable without `downlevelIteration`.
      for (const conversationId of Array.from(knownConversationIds)) {
        if (unreads.has(conversationId)) {
          count++;
        }
      }
      return count;
    },
    [unreads],
  );

  const contextImplementation: ConversationsContextValue = useMemo(() => {
    return {
      getConversationSummary: (conversationId: string) => {
        // Pure read — must not trigger fetches or state updates here, since
        // it's called during render. Consumers populate the cache by calling
        // `ensureConversationSummary` from a `useEffect`.
        const stored = summaries.get(conversationId);
        if (!stored) {
          return { isLoading: true, summary: null };
        }
        return summaryFromStoredSummary(stored, unreads.has(conversationId));
      },
      ensureConversationSummary,
      getUnreadConversationCount,
    } satisfies ConversationsContextValue;
  }, [
    ensureConversationSummary,
    getUnreadConversationCount,
    summaries,
    unreads,
  ]);

  return (
    <ConversationsContext.Provider value={contextImplementation}>
      {children}
    </ConversationsContext.Provider>
  );
}

/**
 * Returns the cached ConversationSummary for a TalkJS conversation, or null
 * while the initial `.set()`/`.get()` is in flight or if the snapshot can't be
 * loaded. Triggers a one-time fetch on mount per conversation ID. Inbound
 * messages (`onMessage`) refresh `lastMessage`; unread state is read from the
 * separate `unreads` set. Both keep the result current for the lifetime of the
 * provider.
 */
export function useConversationSummary(
  conversationId: string,
): ReturnType<ConversationsContextValue['getConversationSummary']> {
  const { ensureConversationSummary, getConversationSummary } =
    useContext(ConversationsContext);
  // Trigger the one-time fetch from an effect — calling it inline during
  // render would queue a state update on the provider while React is mid-
  // render of this consumer, producing a "Cannot update a component while
  // rendering a different component" warning.
  useEffect(() => {
    ensureConversationSummary(conversationId);
  }, [ensureConversationSummary, conversationId]);
  return getConversationSummary(conversationId);
}

/**
 * Returns how many of `knownConversationIds` are currently unread for the
 * user. Reactively updates as TalkJS emits unread-state changes. See
 * `ConversationsContextValue.getUnreadConversationCount` for why the caller
 * supplies the ids.
 */
export function useUnreadConversationCount(
  knownConversationIds: Iterable<string>,
): number {
  return useContext(ConversationsContext).getUnreadConversationCount(
    knownConversationIds,
  );
}
